import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, CircularProgress } from '@mui/material';
import { useAppSelector } from '../../../../app/store';
import { useCreateEncounterMutation } from '../../../../services/EncounterService';

interface CreateEncounterButtonProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

const CreateEncounterButton: React.FC<CreateEncounterButtonProps> = ({ onSuccess, onError }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const mrn = searchParams.get('mrn') || '';
  
  const encounter = useAppSelector((state) => state.encounter.currentEncounter);
  const [createEncounter, { isLoading }] = useCreateEncounterMutation();

  const handleSubmit = async () => {
    if (!encounter) {
      onError?.('No encounter data available');
      return;
    }

    // Validate that encounter has at least one procedure
    if (!encounter.procedures || encounter.procedures.length === 0) {
      onError?.('At least one procedure is required before creating an encounter');
      return;
    }

    try {
      await createEncounter({ encounterView: encounter }).unwrap();
      onSuccess?.();
      
      // Navigate back to registration view if MRN was provided
      if (mrn) {
        setTimeout(() => {
          navigate(`/Registration?company=${company}&center=${center}&mrn=${mrn}`);
        }, 2000);
      }
    } catch (err: any) {
      const errorMessage = err?.data?.message || 'Failed to create encounter.';
      onError?.(errorMessage);
    }
  };

  return (
    <Button
      variant="contained"
      onClick={handleSubmit}
      disabled={isLoading || !encounter}
      sx={{
        backgroundColor: '#162346',
        color: 'white',
        fontWeight: 600,
        px: 3,
        py: 1,
        '&:hover': {
          backgroundColor: '#1a2a52'
        }
      }}
    >
      {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Create Encounter'}
    </Button>
  );
};

export default CreateEncounterButton;
